import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Edit,
  Eye,
  Loader2,
  MoreVertical,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Trash2,
  UserPlus,
  Wrench,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DefectAssignableUserItems } from "./DefectAssignableUserItems";
import { canVerifyDefect } from "@/lib/defectVerification";
import type { DefectCardData, DefectCardProps } from "./DefectCard.types";

type DefectCardActionProps = Pick<
  DefectCardProps,
  | "showMenu"
  | "onView"
  | "onEdit"
  | "onResolve"
  | "onVerify"
  | "onStartProgress"
  | "onCancel"
  | "onReopen"
  | "onDelete"
  | "inspectionStatus"
  | "currentUserId"
  | "onAssign"
  | "assignableUsers"
  | "isQualitaetsmanager"
  | "currentUserTeamIds"
  | "readOnly"
>;

interface DefectCardActionsProps extends DefectCardActionProps {
  defect: DefectCardData;
}

export function DefectCardActions(props: DefectCardActionsProps) {
  // TES-565: read-only roles (customer_approver / Kundenabnahme) get NO
  // status-change controls — only the inline view affordance for closed/
  // cancelled defects and a view-only action menu. The detail dialog handles
  // photo-upload + comments for these users.
  if (props.readOnly) {
    return (
      <>
        <ClosedViewAction {...props} />
        <ReadOnlyActionMenu {...props} />
      </>
    );
  }

  return (
    <>
      <StartProgressAction {...props} />
      <ResolveAction {...props} />
      <VerifyAction {...props} />
      <AssignAction {...props} />
      <ClosedViewAction {...props} />
      <DefectActionMenu {...props} />
    </>
  );
}

/**
 * Shared dropdown shell (trigger button + content surface) for the defect
 * action menus. Extracting it keeps the full and read-only menus from
 * duplicating the trigger markup.
 */
function ActionMenuShell({ defectId, children }: { defectId: string; children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0"
          aria-label={t("common.moreActions")}
          data-testid={`defect-menu-${defectId}`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * TES-565: A minimal action menu for read-only defect users that only exposes
 * the "show details" entry (where they can add photos/comments). All write
 * actions (edit, assign, cancel, reopen, delete) are omitted.
 */
function ReadOnlyActionMenu(props: DefectCardActionsProps) {
  const { showMenu = true, onView } = props;

  if (!showMenu || !onView) {
    return null;
  }

  return (
    <ActionMenuShell defectId={props.defect.id}>
      <ViewMenuItem {...props} />
    </ActionMenuShell>
  );
}

function StartProgressAction({ defect, currentUserId, onStartProgress }: DefectCardActionsProps) {
  const { t } = useTranslation();
  const [startingProgress, setStartingProgress] = useState(false);
  const startProgress = onStartProgress;

  if (!startProgress) {
    return null;
  }

  const canStart = [
    defect.status === "open",
    Boolean(currentUserId),
    defect.assigned_to === currentUserId,
  ].every(Boolean);

  if (!canStart) {
    return null;
  }

  return (
    <Button
      size="sm"
      variant="default"
      disabled={startingProgress}
      onClick={async () => {
        setStartingProgress(true);
        try {
          await startProgress(defect);
        } finally {
          setStartingProgress(false);
        }
      }}
    >
      {startingProgress ? (
        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
      ) : (
        <PlayCircle className="h-4 w-4 mr-1" />
      )}
      {t("defects.actions.startProgress")}
    </Button>
  );
}

function ResolveAction({ defect, currentUserId, onResolve }: DefectCardActionsProps) {
  const { t } = useTranslation();
  const resolveDefect = onResolve;

  if (!resolveDefect) {
    return null;
  }

  const canResolve = [
    defect.status === "in_progress",
    Boolean(currentUserId),
    defect.assigned_to === currentUserId,
  ].every(Boolean);

  if (!canResolve) {
    return null;
  }

  return (
    <Button
      size="sm"
      className="bg-success hover:bg-success/90 text-success-foreground"
      onClick={() => resolveDefect(defect)}
    >
      <Wrench className="h-4 w-4 mr-1" />
      {t("defects.actions.resolveDefect")}
    </Button>
  );
}

function VerifyAction({
  defect,
  currentUserId,
  isQualitaetsmanager = false,
  currentUserTeamIds,
  onVerify,
}: DefectCardActionsProps) {
  const { t } = useTranslation();
  const verifyDefect = onVerify;

  if (!verifyDefect) {
    return null;
  }

  // TES-1141: the assigned verifier OR any team QM may verify in one step.
  const canVerify = canVerifyDefect(defect, currentUserId, {
    isQualitaetsmanager,
    userTeamIds: currentUserTeamIds,
  });

  if (!canVerify) return null;

  return (
    <Button size="sm" variant="default" onClick={() => verifyDefect(defect)}>
      <ShieldCheck className="h-4 w-4 mr-1" />
      {t("defects.actions.verify")}
    </Button>
  );
}

function AssignAction({ defect, onAssign, assignableUsers = [] }: DefectCardActionsProps) {
  const { t } = useTranslation();
  const assignDefect = onAssign;

  if (!assignDefect) {
    return null;
  }

  const canAssign = [
    !defect.assigned_to,
    assignableUsers.length > 0,
    ["open", "in_progress"].includes(defect.status),
  ].every(Boolean);

  if (!canAssign) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="gap-1.5 bg-info hover:bg-info/90 text-info-foreground">
          <UserPlus className="h-4 w-4" />
          {t("common.assign")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
        <DefectAssignableUserItems
          defectId={defect.id}
          assignableUsers={assignableUsers}
          onAssign={assignDefect}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ClosedViewAction({ defect, onView }: DefectCardActionsProps) {
  const { t } = useTranslation();
  const viewDefect = onView;

  if (!viewDefect) {
    return null;
  }

  if (!["closed", "cancelled"].includes(defect.status)) {
    return null;
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="gap-1.5"
      onClick={() => viewDefect(defect)}
      data-testid={`defect-view-inline-${defect.id}`}
    >
      <Eye className="h-4 w-4" />
      {t("inspections.view")}
    </Button>
  );
}

function DefectActionMenu(props: DefectCardActionsProps) {
  const { showMenu = true, onView, onEdit, onCancel, onAssign } = props;
  const hasMenuAction = [onView, onEdit, onCancel, onAssign].some(Boolean);

  if (!showMenu) {
    return null;
  }

  if (!hasMenuAction) {
    return null;
  }

  return (
    <ActionMenuShell defectId={props.defect.id}>
      <ViewMenuItem {...props} />
      <EditMenuItem {...props} />
      <HeaderSeparator {...props} />
      <AssignSubMenu {...props} />
      <CancelMenuItem {...props} />
      <ReopenMenuItem {...props} />
      <DeleteMenuItem {...props} />
    </ActionMenuShell>
  );
}

function ViewMenuItem({ defect, onView }: DefectCardActionsProps) {
  const { t } = useTranslation();
  if (!onView) return null;

  return (
    <DropdownMenuItem onClick={() => onView(defect)} data-testid={`defect-view-${defect.id}`}>
      <Eye className="h-4 w-4 mr-2" />
      {t("inspections.showDetails")}
    </DropdownMenuItem>
  );
}

function EditMenuItem({ defect, onEdit }: DefectCardActionsProps) {
  const { t } = useTranslation();
  if (!onEdit || defect.status === "closed" || defect.status === "cancelled") {
    return null;
  }

  return (
    <DropdownMenuItem onClick={() => onEdit(defect)} data-testid={`defect-edit-${defect.id}`}>
      <Edit className="h-4 w-4 mr-2" />
      {t("common.edit")}
    </DropdownMenuItem>
  );
}

function HeaderSeparator({ onView, onEdit, onAssign, onCancel }: DefectCardActionsProps) {
  const hasReadAction = [onView, onEdit].some(Boolean);
  const hasWriteAction = [onAssign, onCancel].some(Boolean);

  if (!hasReadAction) {
    return null;
  }

  if (!hasWriteAction) {
    return null;
  }

  return <DropdownMenuSeparator />;
}

function AssignSubMenu({ defect, onAssign, assignableUsers = [] }: DefectCardActionsProps) {
  const { t } = useTranslation();
  const assignDefect = onAssign;

  if (!assignDefect) {
    return null;
  }

  const canAssign = [
    assignableUsers.length > 0,
    ["open", "in_progress"].includes(defect.status),
  ].every(Boolean);

  if (!canAssign) {
    return null;
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <UserPlus className="h-4 w-4 mr-2" />
        {t("defects.assignTo")}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
          <DefectAssignableUserItems
            defectId={defect.id}
            assignableUsers={assignableUsers}
            assignedTo={defect.assigned_to}
            includeRemoveAssignment
            onAssign={assignDefect}
          />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

function CancelMenuItem({
  defect,
  currentUserId,
  isQualitaetsmanager = false,
  onCancel,
}: DefectCardActionsProps) {
  const { t } = useTranslation();
  const cancelDefect = onCancel;

  if (!cancelDefect) {
    return null;
  }

  // TES-2229 (LH §L5.10): Client-Pre-Gate — Storno-Urteil fällt der Server
  // (rpc_cancel_defect: Ersteller-im-Fenster ∨ QM ∨ Verifizierer/Deputy).
  // QM sieht die Affordance jetzt auch als Nicht-Ersteller (S5); der Ersteller
  // außerhalb des Korrektur-Fensters bekommt die präzise Server-Ablehnung.
  const canCancel = [
    Boolean(currentUserId),
    defect.created_by === currentUserId || isQualitaetsmanager,
    !["closed", "cancelled"].includes(defect.status),
  ].every(Boolean);

  if (!canCancel) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => cancelDefect(defect)} className="text-destructive">
        <XCircle className="h-4 w-4 mr-2" />
        {t("defects.actions.cancel")}
      </DropdownMenuItem>
    </>
  );
}

function ReopenMenuItem({
  defect,
  currentUserId,
  inspectionStatus,
  onReopen,
}: DefectCardActionsProps) {
  const { t } = useTranslation();
  const reopenDefect = onReopen;

  if (!reopenDefect) {
    return null;
  }

  const canReopen = [
    Boolean(currentUserId),
    [defect.created_by, defect.verifying_qm].includes(currentUserId ?? ""),
    ["closed", "cancelled"].includes(defect.status),
    inspectionStatus !== "cancelled",
  ].every(Boolean);

  if (!canReopen) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => reopenDefect(defect)} className="text-primary">
        <RotateCcw className="h-4 w-4 mr-2" />
        {t("defects.actions.reopen")}
      </DropdownMenuItem>
    </>
  );
}

function DeleteMenuItem({
  defect,
  currentUserId,
  inspectionStatus,
  onDelete,
}: DefectCardActionsProps) {
  const { t } = useTranslation();
  const deleteDefect = onDelete;

  if (!deleteDefect) {
    return null;
  }

  const inspectionAllowsDelete = [
    !inspectionStatus,
    !["completed", "cancelled"].includes(inspectionStatus ?? ""),
  ].some(Boolean);

  // TES-2229 (LH §L5.10, Scope 4): Soft-Delete NUR Ersteller im Sofort-Korrektur-Fenster —
  // Client-Pre-Gate auf status='open' (Fenster-Grundbedingung); die volle Fremd-Interaktions-
  // Prüfung erzwingt der DB-Guard guard_defect_soft_delete (defect_creator_correction_window).
  const canDelete = [
    Boolean(currentUserId),
    defect.created_by === currentUserId,
    defect.status === "open",
    inspectionAllowsDelete,
  ].every(Boolean);

  if (!canDelete) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => deleteDefect(defect)} className="text-destructive">
        <Trash2 className="h-4 w-4 mr-2" />
        {t("common.delete")}
      </DropdownMenuItem>
    </>
  );
}
